/* eslint-env browser */
import { useEffect, useRef } from "react";

/*
  The address bar follows the section in view, Stripe style.

  The URL names the last heading whose top is at or above the reading line.
  The reading line is where a jump to a heading lands: the scroll padding of
  <html> plus the scroll margin of the heading. The rule therefore agrees with
  a jump, so a page that opens at a section keeps that section in the URL.

  The sidebar highlight comes from Zudoku's own IntersectionObserver, which
  uses a band instead of a line, so it can name another entry near the top and
  the bottom of the page.

  The URL is written with history.replaceState only. The router does not
  navigate, so nothing re-renders, scrolls, or flashes a `#hash`.
*/

// Covers rounding and sub-pixel layout around the landing position.
const SLACK = 4;

const pixels = (value: string) => {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

export type UrlSync = {
  /** The section headings, in page order. */
  headings: () => HTMLElement[];
  /** The URL of a section, or of the top of the page when `id` is undefined. */
  toUrl: (id?: string) => string;
  /** While this returns true the URL is left alone; a jump is still running. */
  paused?: () => boolean;
};

export const useUrlFollowsScroll = ({ headings, toUrl, paused }: UrlSync) => {
  useEffect(() => {
    const elements = headings();
    if (elements.length === 0) return;

    const landingLine =
      pixels(getComputedStyle(document.documentElement).scrollPaddingTop) +
      pixels(getComputedStyle(elements[0]!).scrollMarginTop) +
      SLACK;

    const show = (id?: string) => {
      const url = new URL(toUrl(id), window.location.href);
      url.search = window.location.search;
      if (url.href === window.location.href) return;
      window.history.replaceState(window.history.state, "", url);
    };

    const sectionInView = () => {
      if (window.scrollY === 0) return undefined;
      const atBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight;
      if (atBottom) return elements.at(-1)?.id;

      let inView: HTMLElement | undefined;
      for (const element of elements) {
        if (element.getBoundingClientRect().top > landingLine) break;
        inView = element;
      }
      return inView?.id;
    };

    let frame = 0;
    const sync = () => {
      frame = 0;
      if (!paused?.()) show(sectionInView());
    };
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(sync);
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    // One frame later the jump of the page that just opened has landed.
    const first = requestAnimationFrame(sync);

    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [headings, toUrl, paused]);
};

/*
  Opens the page at `id` and keeps it there for a moment.

  One `scrollIntoView` is not enough. After a client-side navigation the router
  scrolls to the top, and on Back it restores the position it remembers; the
  API operation lists render with `content-visibility: auto`, so the page is
  still growing under the jump and the first one lands short. Repeat the jump
  for a few dozen frames, and give way to the reader on the first input event.

  The returned ref is true while the loop runs. Pass it to
  `useUrlFollowsScroll` as `paused`, so the URL is not rewritten from a
  position the loop is about to leave. The URL already names `id`.
*/
const SETTLE_FRAMES = 40;
const READER_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];

export const useJumpTo = (id: string | undefined) => {
  const settling = useRef(false);

  useEffect(() => {
    if (!id) return;

    let frame = 0;
    let left = SETTLE_FRAMES;
    const stop = () => {
      cancelAnimationFrame(frame);
      settling.current = false;
    };
    const step = () => {
      document.getElementById(id)?.scrollIntoView();
      if (left-- > 0) frame = requestAnimationFrame(step);
      else settling.current = false;
    };
    settling.current = true;
    frame = requestAnimationFrame(step);

    for (const event of READER_EVENTS)
      window.addEventListener(event, stop, { passive: true });

    return () => {
      stop();
      for (const event of READER_EVENTS) window.removeEventListener(event, stop);
    };
  }, [id]);

  return settling;
};
